<template>
  <ValidationObserver ref="skills">
    <div>
      <div>
        <h2 class="mb-5">
          Which of these skills do you have?
        </h2>
      </div>
      <div v-if="isLoading">
        <v-skeleton-loader type="list-item-three-line" />
      </div>
      <div v-else>
        <div
          v-for="(categoryItems, category) in formattedSkills"
          :key="category"
        >
          <p class="font-weight-bold">{{ category | capitalize }}</p>
          <div class="checkbox-wrapper col-12">
            <div
              v-for="(item, index) in categoryItems"
              :key="index"
              :class="{
                'checkbox--large__active': isActive(item.value)
              }"
              class="checkbox checkbox--large mixed"
            >
              <v-checkbox
                @click="setValue(item.value)"
                :label="item.label"
                :input-value="selectedSkills"
                :value="item.value"
                on-icon="mdi-check"
                color="black"
                dense
              />
              <ValidationProvider
                v-slot="{ errors }"
                rules="required"
                v-if="isActive(item.value)"
              >
                <Select
                  item-text="name"
                  item-value="name"
                  v-model="selectedSkillsAndLevels[item.value]"
                  @change="lvl => assignLevelToSkill(lvl, item.value)"
                  placeholder=""
                  :items="skillLevels"
                  :error-messages="showValidationErrors ? errors : []"
                  autocomplete
                />
              </ValidationProvider>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-end">
        <PrimaryButton @click.native="nextStep" :loading="isAddingUserSkills">
          {{ submitButtonTitle || "Continue" }}
        </PrimaryButton>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import { createNamespacedHelpers } from "vuex";
import {
  FETCH_ALL_SKILLS,
  FETCH_SKILL_LEVELS,
  ADD_USER_SKILLS,
  FETCH_USER_SKILLS
} from "@/store/modules/users/languagesAndSkills/actions";
import { LANGUAGES_AND_SKILLS_NAMESPACE } from "@/store/modules/users/languagesAndSkills";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import { copyState } from "@/utils/helpers";
import Select from "@/components/common/Select";
import Vue from "vue";
import { map, size } from "lodash";

const {
  mapActions,
  mapState: mapLanguagesAndSkillsState
} = createNamespacedHelpers(LANGUAGES_AND_SKILLS_NAMESPACE);
const { mapState: mapAuthState } = createNamespacedHelpers(AUTH_NAMESPACE);

export default {
  name: "Skills",
  props: {
    submitButtonTitle: String
  },
  components: {
    PrimaryButton,
    Select,
    ValidationProvider,
    ValidationObserver
  },
  async created() {
    this.isLoading = true;
    await Promise.all([
      this.fetchAllSkills(),
      this.fetchSkillLevels(),
      this.fetchUserSkills(this.self.id)
    ]);
    this.isLoading = false;
    this.adjustInitialSelectedSkillsAndLevels();
  },
  data() {
    return {
      selectedSkillsAndLevels: {},
      isAddingUserSkills: false,
      isLoading: false,
      showValidationErrors: false
    };
  },
  computed: {
    ...mapAuthState({
      self: state => state.userData
    }),
    ...mapLanguagesAndSkillsState(
      copyState(["allSkills", "skillLevels", "currentUserSkills"])
    ),
    selectedSkills() {
      return Object.keys(this.selectedSkillsAndLevels);
    },
    formattedSkills() {
      return this.allSkills.reduce((formattedSkills, skill) => {
        const { category: categoryName, name, id } = skill;
        const functionItem = {
          label: name,
          value: id
        };
        if (formattedSkills[categoryName]) {
          formattedSkills[categoryName].push(functionItem);
        } else {
          formattedSkills[categoryName] = [functionItem];
        }
        return formattedSkills;
      }, {});
    }
  },
  methods: {
    ...mapActions({
      fetchAllSkills: FETCH_ALL_SKILLS,
      fetchSkillLevels: FETCH_SKILL_LEVELS,
      addUserSkills: ADD_USER_SKILLS,
      fetchUserSkills: FETCH_USER_SKILLS
    }),
    adjustInitialSelectedSkillsAndLevels() {
      this.selectedSkillsAndLevels = this.currentUserSkills.reduce(
        (selectedSkillsAndLevels, skill) => {
          selectedSkillsAndLevels[skill.id] = skill.level;
          return selectedSkillsAndLevels;
        },
        {}
      );
    },
    isActive(value) {
      return this.selectedSkills.includes(value);
    },
    assignLevelToSkill(lvl, skillId) {
      this.selectedSkillsAndLevels[skillId] = lvl;
    },
    setValue(value) {
      if (this.selectedSkills.includes(value)) {
        Vue.delete(this.selectedSkillsAndLevels, value);
      } else {
        Vue.set(this.selectedSkillsAndLevels, value, "");
      }
    },
    getFormattedSkills() {
      return map(Object.keys(this.selectedSkillsAndLevels), skillId => ({
        skillId,
        level: this.selectedSkillsAndLevels[skillId]
      }));
    },
    async nextStep() {
      const isValid = await this.$refs.skills.validate();
      this.showValidationErrors = true;
      if (isValid) {
        if (size(this.selectedSkills)) {
          this.isAddingUserSkills = true;
          await this.addUserSkills({
            data: {
              skills: this.getFormattedSkills()
            },
            userId: this.self.id
          });
          this.showValidationErrors = false;
          this.isAddingUserSkills = false;
          this.$emit("nextStep");
        } else {
          this.$emit("nextStep");
        }
      }
    }
  }
};
</script>

<style lang="scss"></style>
